import { SxProps } from "@mui/material";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { ReactNode } from "react";

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.text.primary,
    fontWeight: "bold",
    textAlign: "left",
    whiteSpace: "nowrap",
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textAlign: "left",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

type HeaderDefinition = {
  key: string;
  value: string;
  width?: string;
  render?: (row: Record<string, unknown>) => ReactNode;
};

type CustomizedTablesProps = {
  headers: HeaderDefinition[];
  rows: Record<string, unknown>[];
  sx?: SxProps;
};

export const CustomizedTables = ({
  headers,
  rows,
  sx,
}: CustomizedTablesProps) => {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${theme.palette.grey[100]}`,
        marginTop: "1.25rem",
        marginBottom: "0.5rem",
        ...(sx ?? {}),
      }}
    >
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <StyledTableCell
                align="right"
                key={index}
                sx={header.width ? { width: header.width } : undefined}
              >
                {header.value}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <StyledTableRow key={index}>
              {headers.map((header, idx) => {
                // If header has a custom render function, use it
                if (header.render) {
                  return (
                    <StyledTableCell
                      align="right"
                      key={idx}
                      sx={header.width ? { width: header.width } : undefined}
                    >
                      {header.render(row)}
                    </StyledTableCell>
                  );
                }

                const data = row?.[header.key];
                return (
                  <StyledTableCell
                    align="right"
                    key={idx}
                    sx={header.width ? { width: header.width } : undefined}
                  >
                    {typeof data === "string" || typeof data === "number"
                      ? data
                      : null}
                  </StyledTableCell>
                );
              })}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
